import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { PriceChart } from '../components/PriceChart';
import MobileChartWrapper from '../components/mobile/MobileChartWrapper';
import { useDevice } from '../hooks/useDevice';
import { Prediction } from '../types/predictions';
import { darkTheme } from '../theme';
import { media } from '../theme';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// Types
interface UserData {
  x_handle: string;
  name: string;
  profile_image: string;
  dashboard_unlocked: boolean;
  is_existing_influencer: boolean;
}

interface TweetData {
  id: string;
  tweet_id: string;
  content: string;
  created_at: string;
  is_bitcoin_related: boolean;
  is_prediction: boolean;
  retweet_count: number;
  like_count: number;
  reply_count: number;
  impression_count: number;
  sentiment_score: number;
  prediction_type?: string;
  price_target?: number;
  target_date?: string;
}

interface DashboardStats {
  total_tweets: number;
  bitcoin_tweets: number;
  bitcoin_percentage: number;
  predictions_count: number;
  predictions_correct: number;
  predictions_incorrect: number;
  accuracy_rate: number;
  total_engagement: number;
  avg_engagement: number;
  first_tweet_date: string;
  last_tweet_date: string;
}

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%);
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(247, 147, 26, 0.2);
  
  @media (max-width: 768px) {
    padding: 15px 20px;
    flex-wrap: wrap;
    gap: 10px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  img {
    width: 40px;
    height: 40px;
  }
  
  span {
    font-size: 1.2rem;
    font-weight: 700;
    background: linear-gradient(135deg, #f7931a, #ffb74d);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Avatar = styled.img`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  border: 2px solid #f7931a;
`;

const UserDetails = styled.div`
  text-align: right;
  
  .name {
    font-weight: 600;
    font-size: 1rem;
  }
  
  .handle {
    color: #888;
    font-size: 0.85rem;
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #888;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  
  &:hover {
    border-color: #f7931a;
    color: #f7931a;
  }
`;

const MainContent = styled.main`
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 20px;
  
  h1 {
    font-size: clamp(1.8rem, 5vw, 2.5rem);
    margin-bottom: 10px;
    background: linear-gradient(135deg, #f7931a, #ffb74d);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  p {
    color: #aaa;
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

const HowItWorks = styled.div`
  background: linear-gradient(135deg, rgba(247, 147, 26, 0.1), rgba(255, 183, 77, 0.05));
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  
  .header {
    font-size: 0.9rem;
    color: #f7931a;
    font-weight: 600;
    margin-bottom: 12px;
  }
  
  .steps {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
    }
  }
  
  .step {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .number {
      background: rgba(247, 147, 26, 0.3);
      color: #f7931a;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      font-weight: 700;
      flex-shrink: 0;
    }
    
    .text {
      color: #ccc;
      font-size: 0.85rem;
      
      @media (max-width: 768px) {
        font-size: 0.8rem;
      }
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.div<{ highlight?: boolean }>`
  background: ${props => props.highlight 
    ? 'linear-gradient(135deg, rgba(247, 147, 26, 0.15), rgba(255, 183, 77, 0.1))'
    : 'rgba(255, 255, 255, 0.03)'
  };
  border: 1px solid ${props => props.highlight 
    ? 'rgba(247, 147, 26, 0.4)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 16px;
  padding: 25px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
  
  .icon {
    font-size: 2rem;
    margin-bottom: 10px;
  }
  
  .value {
    font-size: 2rem;
    font-weight: 700;
    color: ${props => props.highlight ? '#f7931a' : '#fff'};
    margin-bottom: 5px;
  }
  
  .label {
    color: #888;
    font-size: 0.9rem;
  }
`;

const Section = styled.section`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 25px;
  margin-bottom: 30px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 15px;
  }
  
  h2 {
    font-size: 1.3rem;
    display: flex;
    align-items: center;
    gap: 10px;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
    
    span {
      font-size: 1.5rem;
      
      @media (max-width: 768px) {
        font-size: 1.2rem;
      }
    }
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 15px;
  }
  
  input {
    flex: 1;
    padding: 12px 16px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: #fff;
    font-size: 0.95rem;
    
    @media (max-width: 768px) {
      padding: 10px 14px;
      font-size: 0.9rem;
    }
    
    &:focus {
      outline: none;
      border-color: #f7931a;
    }
    
    &::placeholder {
      color: #666;
    }
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin-bottom: 15px;
  }
`;

const FilterTab = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #f7931a, #ffb74d)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 1px solid ${props => props.active 
    ? 'transparent' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 20px;
  color: ${props => props.active ? '#000' : '#aaa'};
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
    border-radius: 16px;
  }
  font-size: 0.85rem;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: #f7931a;
    color: ${props => props.active ? '#000' : '#f7931a'};
  }
`;

const TweetList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 600px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    gap: 10px;
    max-height: 400px;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(247, 147, 26, 0.5);
    border-radius: 3px;
  }
`;

const TweetCard = styled.div<{ isPrediction?: boolean; isSelected?: boolean }>`
  background: ${props => props.isSelected 
    ? 'rgba(247, 147, 26, 0.1)' 
    : props.isPrediction 
      ? 'rgba(29, 161, 242, 0.05)' 
      : 'rgba(255, 255, 255, 0.02)'
  };
  border: 1px solid ${props => props.isSelected 
    ? '#f7931a' 
    : props.isPrediction 
      ? 'rgba(29, 161, 242, 0.3)' 
      : 'rgba(255, 255, 255, 0.08)'
  };
  border-radius: 12px;
  padding: 18px;
  cursor: pointer;
  transition: all 0.2s;
  
  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 10px;
  }
  
  &:hover {
    background: rgba(247, 147, 26, 0.05);
    border-color: rgba(247, 147, 26, 0.3);
  }
`;

const TweetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;

const TweetDate = styled.span`
  color: #666;
  font-size: 0.8rem;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const TweetBadges = styled.div`
  display: flex;
  gap: 6px;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const Badge = styled.span<{ type: 'bitcoin' | 'prediction' | 'correct' | 'incorrect' }>`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  
  @media (max-width: 768px) {
    padding: 2px 6px;
    font-size: 0.6rem;
  }
  
  ${props => {
    switch(props.type) {
      case 'bitcoin':
        return 'background: rgba(247, 147, 26, 0.2); color: #f7931a;';
      case 'prediction':
        return 'background: rgba(29, 161, 242, 0.2); color: #1DA1F2;';
      case 'correct':
        return 'background: rgba(76, 175, 80, 0.2); color: #4CAF50;';
      case 'incorrect':
        return 'background: rgba(244, 67, 54, 0.2); color: #f44336;';
      default:
        return '';
    }
  }}
`;

const TweetContent = styled.p`
  color: #e0e0e0;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.4;
    margin-bottom: 8px;
  }
`;

const TweetStats = styled.div`
  display: flex;
  gap: 20px;
  color: #666;
  font-size: 0.8rem;
  
  @media (max-width: 768px) {
    gap: 12px;
    font-size: 0.7rem;
  }
  
  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const ReadOnlyBanner = styled.div`
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 193, 7, 0.1));
  border: 1px solid rgba(255, 152, 0, 0.4);
  border-radius: 12px;
  padding: 15px 20px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 15px;
  
  .message {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #ffb74d;
    
    span {
      font-size: 1.2rem;
    }
  }
  
  button {
    background: linear-gradient(135deg, #f7931a, #ffb74d);
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    color: #000;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(247, 147, 26, 0.4);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 20px;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(247, 147, 26, 0.2);
  border-top: 3px solid #f7931a;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 20px;
  text-align: center;
  
  h2 {
    color: #f44336;
  }
  
  p {
    color: #888;
  }
  
  button {
    background: linear-gradient(135deg, #1DA1F2, #0d8bd9);
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    color: #fff;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  gap: 30px;
  text-align: center;
  padding: 20px;
  
  .logo-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    
    img {
      width: 80px;
      height: 80px;
    }
    
    h1 {
      font-size: 2rem;
      margin: 0;
      background: linear-gradient(135deg, #f7931a, #ffcd00);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  
  .info-section {
    max-width: 400px;
    
    h2 {
      color: #fff;
      font-size: 1.5rem;
      margin-bottom: 10px;
    }
    
    p {
      color: #888;
      font-size: 0.95rem;
      line-height: 1.6;
    }
  }
  
  .benefits {
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: left;
    max-width: 350px;
    
    .benefit {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #ccc;
      font-size: 0.9rem;
      
      .icon {
        font-size: 1.2rem;
      }
    }
  }
  
  .login-button {
    background: #000;
    border: 1px solid #333;
    padding: 14px 28px;
    border-radius: 50px;
    color: #fff;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: all 0.2s;
    
    &:hover {
      background: #111;
      border-color: #555;
      transform: translateY(-2px);
    }
    
    svg {
      width: 20px;
      height: 20px;
      fill: currentColor;
    }
  }
  
  .footer-note {
    color: #666;
    font-size: 0.8rem;
    max-width: 300px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 15px;
  }
`;

// Chart Section Styles - Match main app layout
const ChartSection = styled.div`
  margin-bottom: 30px;
  
  h2 {
    color: #f7931a;
    margin-bottom: 20px;
    font-size: 1.3rem;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ChartLayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(280px, 320px);
  gap: 8px;
  
  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  ${media.tablet} {
    grid-template-columns: 1fr minmax(260px, 300px);
  }
`;

const ChartArea = styled.div`
  display: flex;
  flex-direction: column;
  background: ${props => props.theme?.surface || '#1a1a2e'};
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  border: 1px solid rgba(247, 147, 26, 0.2);
  height: calc(100vh - 400px);
  min-height: 500px;
  max-height: 700px;
  overflow: hidden;
  
  ${media.mobile} {
    height: 400px;
    min-height: 350px;
  }
`;

// Mobile-specific chart area - more compact
const MobileChartArea = styled.div`
  background: ${props => props.theme?.surface || '#1a1a2e'};
  border-radius: 10px;
  border: 1px solid rgba(247, 147, 26, 0.2);
  height: 280px;
  margin-bottom: 16px;
  overflow: hidden;
`;

// Mobile tweet list - vertical scrolling
const MobileTweetList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(247, 147, 26, 0.5);
    border-radius: 2px;
  }
`;

// Mobile tweet card - compact
const MobileTweetCard = styled.div<{ isActive?: boolean }>`
  background: ${props => props.isActive ? 'rgba(247, 147, 26, 0.15)' : 'rgba(0, 0, 0, 0.2)'};
  border: 1px solid ${props => props.isActive ? 'rgba(247, 147, 26, 0.5)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 8px;
  padding: 10px;
  cursor: pointer;
  
  &:active {
    background: rgba(247, 147, 26, 0.1);
  }
  
  .tweet-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    
    .date {
      font-size: 0.7rem;
      color: #666;
    }
    
    .engagement {
      font-size: 0.65rem;
      color: #f7931a;
    }
  }
  
  .tweet-text {
    font-size: 0.8rem;
    color: #ddd;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .tweet-stats {
    display: flex;
    gap: 10px;
    margin-top: 6px;
    font-size: 0.65rem;
    color: #888;
  }
`;

const ChartControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding: 15px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
  
  button {
    background: rgba(247, 147, 26, 0.2);
    border: 1px solid rgba(247, 147, 26, 0.3);
    color: #f7931a;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
    
    @media (max-width: 768px) {
      padding: 8px 14px;
      font-size: 0.9rem;
    }
    
    &:hover {
      background: rgba(247, 147, 26, 0.3);
      transform: translateY(-1px);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  .counter {
    color: #888;
    font-size: 0.9rem;
    padding: 0 15px;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
      padding: 0 10px;
    }
  }
`;

const ChartInfo = styled.div`
  color: #888;
  font-size: 0.85rem;
  
  span {
    color: #f7931a;
  }
  
  .hint {
    margin-top: 5px;
    font-size: 0.8rem;
    color: #666;
  }
`;

// Chart Legend explaining the dots
const ChartLegend = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    margin-bottom: 12px;
  }
  
  .legend-title {
    color: #f7931a;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
      margin-bottom: 8px;
    }
  }
  
  .legend-items {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    
    @media (max-width: 768px) {
      gap: 10px;
    }
  }
  
  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
    color: #aaa;
    
    @media (max-width: 768px) {
      font-size: 0.7rem;
      gap: 5px;
    }
    
    .dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        width: 10px;
        height: 10px;
      }
    }
    
    .dot-active {
      background: #f7931a;
      border: 2px solid #fff;
      box-shadow: 0 0 6px rgba(247, 147, 26, 0.5);
    }
    
    .dot-inactive {
      background: rgba(247, 147, 26, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.3);
    }
    
    .dot-bitcoin {
      background: #f7931a;
      border: 1px solid #fff;
    }
    
    .dot-other {
      background: rgba(100, 100, 100, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
  }
  
  .legend-description {
    margin-top: 10px;
    font-size: 0.75rem;
    color: #666;
    line-height: 1.4;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 10px;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

// Custom Tweet Panel for Dashboard (replaces PredictionPanel)
const TweetPanel = styled.div`
  background: ${props => props.theme?.surface || '#1a1a2e'};
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  border: 1px solid rgba(247, 147, 26, 0.2);
  display: flex;
  flex-direction: column;
  height: calc(100vh - 400px);
  min-height: 500px;
  max-height: 700px;
  overflow: hidden;
  
  ${media.mobile} {
    height: 300px;
    min-height: 250px;
    max-height: 350px;
  }
`;

const TweetPanelHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(247, 147, 26, 0.2);
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  h3 {
    color: #f7931a;
    margin: 0 0 8px 0;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 8px;
    
    @media (max-width: 768px) {
      font-size: 0.95rem;
      margin-bottom: 4px;
    }
  }
  
  .stats {
    display: flex;
    gap: 15px;
    font-size: 0.85rem;
    color: #888;
    
    @media (max-width: 768px) {
      font-size: 0.75rem;
      gap: 10px;
    }
    
    span {
      display: flex;
      align-items: center;
      gap: 5px;
    }
    
    .count {
      color: #f7931a;
      font-weight: 600;
    }
  }
`;

const ChartTweetList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  
  @media (max-width: 768px) {
    padding: 6px;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(247, 147, 26, 0.3);
    border-radius: 3px;
  }
`;

const ChartTweetCard = styled.div<{ isActive?: boolean }>`
  background: ${props => props.isActive ? 'rgba(247, 147, 26, 0.15)' : 'rgba(0, 0, 0, 0.2)'};
  border: 1px solid ${props => props.isActive ? 'rgba(247, 147, 26, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(247, 147, 26, 0.1);
    border-color: rgba(247, 147, 26, 0.3);
  }
  
  .tweet-date {
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 6px;
  }
  
  .tweet-text {
    font-size: 0.85rem;
    color: #ddd;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .tweet-meta {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    font-size: 0.75rem;
    color: #888;
    
    span {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
`;

// Prediction Finder Section
const PredictionFinderSection = styled.div`
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(103, 58, 183, 0.1));
  border: 1px solid rgba(156, 39, 176, 0.3);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 30px;
  
  h3 {
    color: #ce93d8;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .description {
    color: #999;
    font-size: 0.9rem;
    margin-bottom: 20px;
  }
`;

const PotentialPredictionCard = styled.div<{ requested?: boolean }>`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${props => props.requested ? 'rgba(76, 175, 80, 0.5)' : 'rgba(156, 39, 176, 0.2)'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  
  .tweet-date {
    font-size: 0.75rem;
    color: #888;
    margin-bottom: 8px;
  }
  
  .tweet-text {
    font-size: 0.9rem;
    color: #ddd;
    line-height: 1.5;
    margin-bottom: 12px;
  }
  
  .detected-info {
    background: rgba(156, 39, 176, 0.2);
    border-radius: 8px;
    padding: 10px;
    margin-bottom: 12px;
    font-size: 0.85rem;
    
    .price {
      color: #f7931a;
      font-weight: 700;
      font-size: 1.1rem;
    }
    
    .type {
      color: #ce93d8;
      margin-left: 10px;
    }
  }
  
  .actions {
    display: flex;
    gap: 10px;
    align-items: center;
    
    button {
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 0.85rem;
      cursor: pointer;
      transition: all 0.2s;
      
      &.request-btn {
        background: linear-gradient(135deg, #9c27b0, #673ab7);
        border: none;
        color: white;
        font-weight: 600;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(156, 39, 176, 0.4);
        }
        
        &:disabled {
          background: #444;
          cursor: not-allowed;
          transform: none;
        }
      }
      
      &.requested {
        background: rgba(76, 175, 80, 0.2);
        border: 1px solid rgba(76, 175, 80, 0.5);
        color: #81c784;
      }
    }
    
    .status {
      font-size: 0.8rem;
      color: #81c784;
    }
  }
`;

// ========================================
// Analytics/Insights Section Styled Components
// ========================================

const AnalyticsSection = styled.section`
  background: linear-gradient(135deg, rgba(247, 147, 26, 0.05) 0%, rgba(26, 26, 46, 0.8) 100%);
  border: 1px solid rgba(247, 147, 26, 0.2);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 20px;
  }
  
  h2 {
    color: #f7931a;
    margin: 0 0 20px 0;
    font-size: 1.3rem;
    display: flex;
    align-items: center;
    gap: 10px;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 16px;
    }
  }
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 16px;
  }
`;

const InsightCard = styled.div<{ gradient?: string }>`
  background: ${props => props.gradient || 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 12px 8px;
    border-radius: 10px;
  }
  
  &:hover {
    transform: translateY(-3px);
    border-color: rgba(247, 147, 26, 0.3);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
  
  .stat-value {
    font-size: 2rem;
    font-weight: 800;
    background: linear-gradient(135deg, #f7931a, #ff6b00);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 4px;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
  
  .stat-label {
    font-size: 0.85rem;
    color: #aaa;
    font-weight: 500;
    
    @media (max-width: 768px) {
      font-size: 0.75rem;
    }
  }
  
  .stat-detail {
    font-size: 0.75rem;
    color: #666;
    margin-top: 4px;
    
    @media (max-width: 768px) {
      font-size: 0.65rem;
    }
  }
`;

const JourneyTimeline = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(247, 147, 26, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 14px;
    margin-bottom: 16px;
  }
  
  h3 {
    color: #f7931a;
    font-size: 1rem;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      margin-bottom: 12px;
    }
  }
`;

const YearlyChart = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 120px;
  gap: 8px;
  padding-top: 20px;
  
  @media (max-width: 768px) {
    height: 100px;
    gap: 4px;
    padding-top: 15px;
  }
`;

const YearBar = styled.div<{ height: number; isCurrentYear?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  max-width: 50px;
  
  @media (max-width: 768px) {
    max-width: 35px;
    gap: 2px;
  }
  
  .bar-container {
    width: 100%;
    height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    
    @media (max-width: 768px) {
      height: 60px;
    }
    
    .bar {
      width: 100%;
      height: ${props => props.height}%;
      background: ${props => props.isCurrentYear 
        ? 'linear-gradient(180deg, #f7931a, #ff6b00)' 
        : 'linear-gradient(180deg, rgba(247, 147, 26, 0.6), rgba(247, 147, 26, 0.3))'};
      border-radius: 4px 4px 0 0;
      transition: all 0.3s ease;
      min-height: ${props => props.height > 0 ? '4px' : '0'};
    }
    
    .count {
      font-size: 0.7rem;
      color: ${props => props.isCurrentYear ? '#f7931a' : '#888'};
      font-weight: ${props => props.isCurrentYear ? '600' : '400'};
      margin-bottom: 2px;
      
      @media (max-width: 768px) {
        font-size: 0.6rem;
      }
    }
  }
  
  .year-label {
    font-size: 0.7rem;
    color: ${props => props.isCurrentYear ? '#f7931a' : '#666'};
    font-weight: ${props => props.isCurrentYear ? '600' : '400'};
    
    @media (max-width: 768px) {
      font-size: 0.55rem;
    }
  }
`;

const PersonalityBadge = styled.div<{ type?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${props => {
    switch(props.type) {
      case 'maximalist': return 'linear-gradient(135deg, rgba(247, 147, 26, 0.3), rgba(255, 107, 0, 0.2))';
      case 'enthusiast': return 'linear-gradient(135deg, rgba(76, 175, 80, 0.3), rgba(56, 142, 60, 0.2))';
      case 'curious': return 'linear-gradient(135deg, rgba(33, 150, 243, 0.3), rgba(25, 118, 210, 0.2))';
      default: return 'rgba(100, 100, 100, 0.3)';
    }
  }};
  border: 1px solid ${props => {
    switch(props.type) {
      case 'maximalist': return 'rgba(247, 147, 26, 0.5)';
      case 'enthusiast': return 'rgba(76, 175, 80, 0.5)';
      case 'curious': return 'rgba(33, 150, 243, 0.5)';
      default: return 'rgba(150, 150, 150, 0.3)';
    }
  }};
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  
  .emoji {
    font-size: 1.2rem;
  }
`;

const TopTweetCard = styled.div`
  background: rgba(20, 23, 26, 0.95);
  border: 1px solid rgba(56, 68, 77, 0.8);
  border-radius: 16px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 12px;
  }
  
  &:hover {
    background: rgba(25, 28, 31, 0.98);
    border-color: rgba(247, 147, 26, 0.4);
    box-shadow: 0 4px 20px rgba(247, 147, 26, 0.15);
  }
  
  .tweet-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    
    @media (max-width: 768px) {
      gap: 10px;
      margin-bottom: 10px;
    }
    
    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #f7931a, #ff6b00);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      color: white;
      font-size: 1.1rem;
      overflow: hidden;
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        width: 36px;
        height: 36px;
        font-size: 1rem;
      }
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    .user-info {
      flex: 1;
      min-width: 0;
      
      .name {
        color: #e7e9ea;
        font-weight: 700;
        font-size: 0.95rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        
        @media (max-width: 768px) {
          font-size: 0.85rem;
        }
      }
      
      .handle {
        color: #71767b;
        font-size: 0.85rem;
        
        @media (max-width: 768px) {
          font-size: 0.75rem;
        }
      }
    }
    
    .badge {
      font-size: 0.7rem;
      font-weight: 700;
      color: #f91880;
      background: rgba(249, 24, 128, 0.15);
      padding: 4px 10px;
      border-radius: 12px;
      border: 1px solid rgba(249, 24, 128, 0.4);
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        font-size: 0.6rem;
        padding: 3px 8px;
      }
    }
  }
  
  .tweet-text {
    color: #e7e9ea;
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 12px;
    
    @media (max-width: 768px) {
      font-size: 0.85rem;
      line-height: 1.4;
      margin-bottom: 10px;
    }
  }
  
  .tweet-stats {
    display: flex;
    gap: 16px;
    font-size: 0.85rem;
    color: #71767b;
    flex-wrap: wrap;
    
    @media (max-width: 768px) {
      gap: 10px;
      font-size: 0.75rem;
    }
    
    .likes {
      color: #f91880;
    }
  }
`;

const WrappedButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 300px;
  margin: 20px auto 0;
  padding: 14px 24px;
  background: linear-gradient(135deg, #f7931a, #ff6b00);
  border: none;
  border-radius: 50px;
  color: white;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 12px 20px;
    font-size: 0.9rem;
    margin: 16px auto 0;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(247, 147, 26, 0.4);
  }
  
  svg {
    width: 20px;
    height: 20px;
    
    @media (max-width: 768px) {
      width: 18px;
      height: 18px;
    }
  }
`;

// Main Component
const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const [user, setUser] = useState<UserData | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tweets, setTweets] = useState<TweetData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'bitcoin' | 'predictions'>(() => {
    const saved = localStorage.getItem('dashboard_filter');
    return (saved as 'all' | 'bitcoin' | 'predictions') || 'all';
  });
  const [tweetTypeFilter, setTweetTypeFilter] = useState<'all' | 'original' | 'replies' | 'retweets'>(() => {
    const saved = localStorage.getItem('dashboard_tweetTypeFilter');
    return (saved as 'all' | 'original' | 'replies' | 'retweets') || 'all';
  });
  const [timePeriod, setTimePeriod] = useState<'all' | 'week' | 'month' | 'year'>(() => {
    const saved = localStorage.getItem('dashboard_timePeriod');
    return (saved as 'all' | 'week' | 'month' | 'year') || 'all';
  });
  const [showTopOnly, setShowTopOnly] = useState(() => {
    return localStorage.getItem('dashboard_showTopOnly') === 'true';
  });
  const [selectedTweet, setSelectedTweet] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  
  // Custom filter state
  const [customKeywords, setCustomKeywords] = useState<string>('');
  const [savedFilters, setSavedFilters] = useState<Array<{name: string, keywords: string}>>([]);
  const [showCustomFilter, setShowCustomFilter] = useState(false);
  
  // Tweet Analysis / Prediction Finder state
  const [requestedPredictions, setRequestedPredictions] = useState<Set<string>>(new Set());
  const [dismissedPredictions, setDismissedPredictions] = useState<Set<string>>(new Set());
  const [showPredictionFinder, setShowPredictionFinder] = useState(false);
  
  // Chart state
  const [chartData, setChartData] = useState<Array<{ time: string; value: number }>>([]);
  const [chartPredictions, setChartPredictions] = useState<Prediction[]>([]);
  const [activePredictions, setActivePredictions] = useState<number[]>([]);
  const [currentTweetIndex, setCurrentTweetIndex] = useState(-1); // -1 means nothing selected
  
  // User's official predictions (from predictions table, shown on main chart)
  const [officialPredictions, setOfficialPredictions] = useState<any[]>([]);
  
  // Own prediction modal state
  const [owningPrediction, setOwningPrediction] = useState<any | null>(null);
  const [ownExplanation, setOwnExplanation] = useState('');
  const [isOwning, setIsOwning] = useState(false);

  // Load saved filters from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('dashboard_saved_filters');
    if (saved) {
      try {
        setSavedFilters(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load saved filters');
      }
    }
    
    // Load dismissed predictions
    const dismissed = localStorage.getItem('dashboard_dismissed_predictions');
    if (dismissed) {
      try {
        setDismissedPredictions(new Set(JSON.parse(dismissed)));
      } catch (e) {
        console.error('Failed to load dismissed predictions');
      }
    }
  }, []);

  // Persist filter state to localStorage
  useEffect(() => {
    localStorage.setItem('dashboard_filter', filter);
    localStorage.setItem('dashboard_tweetTypeFilter', tweetTypeFilter);
    localStorage.setItem('dashboard_timePeriod', timePeriod);
    localStorage.setItem('dashboard_showTopOnly', String(showTopOnly));
  }, [filter, tweetTypeFilter, timePeriod, showTopOnly]);

  // Dismiss a potential prediction (not a real prediction)
  const handleDismissPrediction = (tweetId: string) => {
    setDismissedPredictions(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(tweetId);
      localStorage.setItem('dashboard_dismissed_predictions', JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  };

  // Helper function to determine tweet type
  const getTweetType = (content: string): 'original' | 'reply' | 'retweet' => {
    if (content.startsWith('RT @')) return 'retweet';
    if (content.startsWith('@')) return 'reply';
    return 'original';
  };

  // Helper to calculate engagement score
  const getEngagementScore = (tweet: TweetData): number => {
    return (tweet.like_count || 0) + (tweet.retweet_count || 0) * 2 + (tweet.reply_count || 0);
  };
  
  // Helper to check if tweet matches custom keywords (supports AND with +, OR with |)
  const matchesCustomKeywords = (content: string, keywords: string): boolean => {
    if (!keywords.trim()) return true;
    const lowerContent = content.toLowerCase();
    
    // Split by | for OR conditions
    const orGroups = keywords.split('|').map(g => g.trim()).filter(g => g);
    
    // For each OR group, check if ALL AND conditions (+) match
    return orGroups.some(group => {
      const andTerms = group.split('+').map(t => t.trim().toLowerCase()).filter(t => t);
      return andTerms.every(term => lowerContent.includes(term));
    });
  };
  
  // Save a custom filter
  const saveCustomFilter = () => {
    if (!customKeywords.trim()) return;
    const name = prompt('Name this filter:');
    if (!name) return;
    
    const newFilters = [...savedFilters, { name, keywords: customKeywords }];
    setSavedFilters(newFilters);
    localStorage.setItem('dashboard_saved_filters', JSON.stringify(newFilters));
  };
  
  // Delete a saved filter
  const deleteSavedFilter = (index: number) => {
    const newFilters = savedFilters.filter((_, i) => i !== index);
    setSavedFilters(newFilters);
    localStorage.setItem('dashboard_saved_filters', JSON.stringify(newFilters));
  };
  
  // Get time period cutoff date
  const getTimeCutoff = (period: string): Date | null => {
    const now = new Date();
    switch (period) {
      case 'week': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case 'month': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case 'year': return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      default: return null;
    }
  };

  // 🎯 PREDICTION FINDER: Detect potential price predictions in tweets
  interface DetectedPrediction {
    tweet: TweetData;
    detectedPrice: number;
    priceText: string;
    predictionType: 'target' | 'top' | 'bottom' | 'range';
    confidence: 'high' | 'medium' | 'low';
  }
  
  const potentialPredictions = useMemo((): DetectedPrediction[] => {
    if (!tweets.length) return [];
    
    const predictions: DetectedPrediction[] = [];
    
    // Regex patterns to detect price predictions
    const pricePatterns = [
      // "$100k", "$150,000", "100k dollars"
      /\$\s*(\d{1,3}(?:,?\d{3})*(?:\.\d+)?)\s*[kK]?/gi,
      // "100k", "150K" (standalone numbers with K)
      /\b(\d{2,3})[kK]\b/gi,
      // "bitcoin to 100000", "btc at 150000"
      /(?:bitcoin|btc|₿)\s+(?:to|at|hits?|reaches?|above|below)\s+\$?\s*(\d{4,7})/gi,
      // Price ranges: "between 80k and 100k"
      /between\s+\$?(\d{2,3})[kK]?\s+and\s+\$?(\d{2,3})[kK]?/gi,
    ];
    
    // Keywords that indicate prediction intent
    const predictionKeywords = [
      'predict', 'target', 'bottom', 'top', 'ath', 'will hit', 'going to',
      'expect', 'see btc', 'see bitcoin', 'calling', 'my call', 'price target',
      'moon', 'dump', 'crash', 'rally', 'bull', 'bear', 'eoy', 'end of year'
    ];
    
    for (const tweet of tweets) {
      const content = tweet.content.toLowerCase();
      
      // Skip retweets (both RT @ and quoted RTs)
      if (content.startsWith('rt @') || content.includes('rt @')) continue;
      
      // Skip replies that are just reactions
      if (content.startsWith('@') && content.length < 100) continue;
      
      // Check for prediction-like language - REQUIRE this for inclusion
      const hasPredictionIntent = predictionKeywords.some(kw => content.includes(kw));
      
      // Must have prediction intent to continue
      if (!hasPredictionIntent) continue;
      
      // Try to extract prices
      let detectedPrice = 0;
      let priceText = '';
      let predictionType: 'target' | 'top' | 'bottom' | 'range' = 'target';
      
      // Look for price mentions
      const priceMatch = tweet.content.match(/\$\s*(\d{1,3}(?:,?\d{3})*)\s*[kK]?/i) 
        || tweet.content.match(/\b(\d{2,3})[kK]\b/i)
        || tweet.content.match(/(\d{5,7})/);
      
      if (priceMatch) {
        let rawPrice = priceMatch[1].replace(/,/g, '');
        detectedPrice = parseInt(rawPrice);
        
        // Handle "k" suffix
        if (priceMatch[0].toLowerCase().includes('k') && detectedPrice < 1000) {
          detectedPrice *= 1000;
        }
        
        // Filter reasonable BTC prices ($10k - $1M)
        if (detectedPrice < 10000 || detectedPrice > 1000000) continue;
        
        priceText = priceMatch[0];
        
        // Determine prediction type
        if (content.includes('bottom') || content.includes('floor') || content.includes('support')) {
          predictionType = 'bottom';
        } else if (content.includes('top') || content.includes('ath') || content.includes('peak') || content.includes('ceiling')) {
          predictionType = 'top';
        }
      } else {
        continue; // No price detected
      }
      
      // Calculate confidence based on stronger prediction signals
      let confidence: 'high' | 'medium' | 'low' = 'medium';
      const strongPredictionWords = ['predict', 'target', 'calling', 'my call', 'price target', 'will hit', 'going to', 'expect'];
      const hasStrongIntent = strongPredictionWords.some(kw => content.includes(kw));
      
      if (hasStrongIntent && detectedPrice > 0) {
        confidence = 'high';
      }
      
      // Only include if we have a price (prediction intent already required above)
      if (detectedPrice > 0) {
        predictions.push({
          tweet,
          detectedPrice,
          priceText,
          predictionType,
          confidence
        });
      }
    }
    
    // Filter out dismissed predictions, then sort by confidence (high first) then by date (newest first)
    return predictions
      .filter(p => !dismissedPredictions.has(p.tweet.tweet_id))
      .sort((a, b) => {
        if (a.confidence !== b.confidence) {
          return a.confidence === 'high' ? -1 : 1;
        }
        return new Date(b.tweet.created_at).getTime() - new Date(a.tweet.created_at).getTime();
      }).slice(0, 20); // Limit to top 20
  }, [tweets, dismissedPredictions]);

  // Handle requesting a prediction to be added to chart (auto-detected)
  const handleRequestPrediction = async (tweet: TweetData, detectedPrice: number, predictionType: string) => {
    const token = localStorage.getItem('jwt_token');
    if (!token || !user) return;
    
    try {
      const response = await fetch('/api/dashboard/request-prediction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tweet_id: tweet.tweet_id,
          tweet_text: tweet.content,
          tweet_date: tweet.created_at,
          detected_price: detectedPrice,
          prediction_type: predictionType,
          source_url: `https://x.com/${user.x_handle}/status/${tweet.tweet_id}`
        })
      });
      
      if (response.ok) {
        setRequestedPredictions(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.add(tweet.tweet_id);
          return newSet;
        });
        alert('✅ Request submitted! We\'ll review and add it to the chart if approved.');
      } else {
        alert('Failed to submit request. Please try again.');
      }
    } catch (err) {
      console.error('Request error:', err);
      alert('Failed to submit request. Please try again.');
    }
  };

  // Handle manual request to add any tweet to the chart
  const handleManualTweetRequest = async (tweet: TweetData) => {
    const token = localStorage.getItem('jwt_token');
    if (!token || !user) return;
    
    try {
      const response = await fetch('/api/dashboard/request-prediction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          tweet_id: tweet.tweet_id,
          tweet_text: tweet.content,
          tweet_date: tweet.created_at,
          detected_price: null, // Admin will determine
          prediction_type: 'manual_request',
          source_url: `https://x.com/${user.x_handle}/status/${tweet.tweet_id}`
        })
      });
      
      if (response.ok) {
        setRequestedPredictions(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.add(tweet.tweet_id);
          return newSet;
        });
        alert('✅ Request submitted! We\'ll review your tweet and add it to the chart if it contains a prediction.');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to submit request. Please try again.');
      }
    } catch (err) {
      console.error('Request error:', err);
      alert('Failed to submit request. Please try again.');
    }
  };

  // Auth check and data fetch
  useEffect(() => {
    // Check for token in URL first (from OAuth callback)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    
    if (urlToken) {
      // Store the token and clean up URL
      localStorage.setItem('jwt_token', urlToken);
      window.history.replaceState({}, document.title, '/dashboard');
    }
    
    const token = urlToken || localStorage.getItem('jwt_token');
    
    if (!token) {
      // No token - show login screen
      setNeedsLogin(true);
      setLoading(false);
      return;
    }

    // Verify token and fetch dashboard data
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token: string) => {
    try {
      setLoading(true);
      setError(null);

      // Decode JWT to get user info (for display before API call completes)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          x_handle: payload.x_handle,
          name: payload.name,
          profile_image: payload.profile_image,
          dashboard_unlocked: payload.has_wrapped || false,
          is_existing_influencer: payload.is_existing_influencer || false
        });
      } catch (e) {
        console.error('Failed to decode JWT:', e);
      }

      // Fetch dashboard data from API
      const response = await fetch(`${apiBase}/api/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('jwt_token');
        navigate('/wrapped');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      
      setUser(data.user);
      setStats(data.stats);
      setTweets(data.tweets || []);
      
      // Load chart data
      loadChartData();
      
      // Convert tweets to chart predictions
      if (data.tweets && data.tweets.length > 0 && data.user) {
        convertTweetsToChartPredictions(data.tweets, data.user);
      }
      
      // Fetch user's official predictions
      fetchOfficialPredictions(token);
      
      setLoading(false);

    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      setLoading(false);
    }
  };

  // Load Bitcoin price data for chart
  const loadChartData = async () => {
    try {
      const response = await fetch(`${apiBase}/api/prices`);
      if (response.ok) {
        const data = await response.json();
        const formattedData = data.map((item: any) => ({
          time: item.date,
          value: item.close
        }));
        setChartData(formattedData);
      }
    } catch (error) {
      console.error('Failed to load chart data:', error);
    }
  };

  // Fetch user's official predictions (from predictions table)
  const fetchOfficialPredictions = async (token: string) => {
    try {
      const response = await fetch(`${apiBase}/api/dashboard/predictions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.predictions) {
          setOfficialPredictions(data.predictions);
          console.log('📊 Official predictions loaded:', data.predictions.length);
        }
      }
    } catch (error) {
      console.error('Failed to fetch official predictions:', error);
    }
  };

  // Own a prediction with explanation
  const handleOwnPrediction = async () => {
    if (!owningPrediction) return;
    
    const token = localStorage.getItem('jwt_token');
    if (!token) return;
    
    setIsOwning(true);
    try {
      const response = await fetch(`${apiBase}/api/dashboard/own-prediction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prediction_id: owningPrediction.id,
          explanation: ownExplanation
        })
      });
      
      if (response.ok) {
        // Update local state
        setOfficialPredictions(prev => prev.map(p => 
          p.id === owningPrediction.id 
            ? { ...p, owned_at: new Date().toISOString(), owner_explanation: ownExplanation }
            : p
        ));
        setOwningPrediction(null);
        setOwnExplanation('');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to own prediction');
      }
    } catch (err) {
      console.error('Own prediction error:', err);
      alert('Failed to own prediction');
    } finally {
      setIsOwning(false);
    }
  };

  // Generate Twitter share URL for owned prediction
  const getTwitterShareUrl = (pred: any) => {
    const isSuccess = pred.success_status === 'success';
    const isMiss = pred.success_status === 'miss';
    const price = Number(pred.price_prediction).toLocaleString();
    const targetDate = pred.target_time ? new Date(pred.target_time).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
    
    let tweetText = '';
    if (isSuccess) {
      tweetText = `✅ I called it! My $${price} Bitcoin prediction was correct.\n\n${pred.owner_explanation || ''}\n\nTrack my predictions on @proofofprice 📊`;
    } else if (isMiss) {
      tweetText = `❌ I own this miss. My $${price} Bitcoin target by ${targetDate} didn't hit.\n\n${pred.owner_explanation || ''}\n\nAccountability matters. Track predictions on @proofofprice 📊`;
    } else {
      tweetText = `🎯 My Bitcoin prediction: $${price} by ${targetDate}\n\n${pred.owner_explanation || ''}\n\nTrack it live on @proofofprice 📊`;
    }
    
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
  };

  // Convert user's tweets to Prediction format for chart visualization
  const convertTweetsToChartPredictions = (userTweets: TweetData[], userData: UserData) => {
    // Show ALL tweets on the chart (not just bitcoin-related)
    const predictions: Prediction[] = userTweets.map((tweet, index) => ({
      id: index + 1,
      prediction_type: tweet.is_prediction ? (tweet.prediction_type as any || 'sentiment') : 'sentiment',
      prediction_made: tweet.created_at,
      created_at: tweet.created_at,
      start_date: tweet.created_at,
      end_date: tweet.created_at,
      price_target: tweet.price_target,
      success_status: 'pending' as const,
      quote: tweet.content,
      source_url: `https://x.com/${userData.x_handle}/status/${tweet.tweet_id}`,
      influencer: {
        id: 0,
        name: userData.name,
        score: 0,
        x_handle: userData.x_handle,
        profile_image: userData.profile_image
      },
      tweet_data: {
        text: tweet.content,
        profile_image_url: userData.profile_image,
        verified: false
      }
    }));
    
    setChartPredictions(predictions);
    // Start with nothing selected - user clicks to select
    setActivePredictions([]);
    setCurrentTweetIndex(-1);
  };

  // Get filtered tweets for chart based on current filter
  const chartFilteredTweets = useMemo(() => {
    let result = tweets;
    
    // Content filter (all/bitcoin/predictions)
    if (filter === 'bitcoin') {
      result = result.filter(t => t.is_bitcoin_related);
    } else if (filter === 'predictions') {
      result = result.filter(t => t.is_prediction);
    }
    
    // Tweet type filter (original/replies/retweets)
    if (tweetTypeFilter === 'original') {
      result = result.filter(t => getTweetType(t.content) === 'original');
    } else if (tweetTypeFilter === 'replies') {
      result = result.filter(t => getTweetType(t.content) === 'reply');
    } else if (tweetTypeFilter === 'retweets') {
      result = result.filter(t => getTweetType(t.content) === 'retweet');
    }
    
    // Time period filter
    const timeCutoff = getTimeCutoff(timePeriod);
    if (timeCutoff) {
      result = result.filter(t => new Date(t.created_at) >= timeCutoff);
    }
    
    // Search query (basic search)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => t.content.toLowerCase().includes(query));
    }
    
    // Custom keyword filter (advanced: supports + for AND, | for OR)
    if (customKeywords.trim()) {
      result = result.filter(t => matchesCustomKeywords(t.content, customKeywords));
    }
    
    // Top Engaged filter (applied last, after all other filters)
    if (showTopOnly) {
      const sorted = [...result].sort((a, b) => getEngagementScore(b) - getEngagementScore(a));
      const topCount = Math.max(10, Math.ceil(sorted.length * 0.1)); // Top 10% or at least 10
      result = sorted.slice(0, topCount);
    }
    
    return result;
  }, [tweets, filter, tweetTypeFilter, timePeriod, searchQuery, customKeywords, showTopOnly]);

  // Calculate max engagement for normalization
  const maxEngagement = useMemo(() => {
    if (chartFilteredTweets.length === 0) return 1;
    return Math.max(...chartFilteredTweets.map(t => getEngagementScore(t)), 1);
  }, [chartFilteredTweets]);

  // Convert filtered tweets to predictions for chart
  const filteredChartPredictions = useMemo(() => {
    if (!user || chartFilteredTweets.length === 0) return [];
    return chartFilteredTweets.map((tweet, index) => {
      const engagementScore = getEngagementScore(tweet);
      const engagementLevel = engagementScore / maxEngagement; // 0-1 normalized
      
      return {
        id: index + 1,
        prediction_type: tweet.is_prediction ? (tweet.prediction_type as any || 'sentiment') : 'sentiment',
        prediction_made: tweet.created_at,
        created_at: tweet.created_at,
        start_date: tweet.created_at,
        end_date: tweet.created_at,
        price_target: tweet.price_target,
        success_status: 'pending' as const,
        quote: tweet.content,
        source_url: `https://x.com/${user.x_handle}/status/${tweet.tweet_id}`,
        influencer: {
          id: 0,
          name: user.name,
          score: 0,
          x_handle: user.x_handle,
          profile_image: user.profile_image
        },
        tweet_data: {
          text: tweet.content,
          profile_image_url: user.profile_image,
          verified: false,
          // Add engagement data for dot sizing
          engagement_score: engagementScore,
          engagement_level: engagementLevel,
          like_count: tweet.like_count || 0,
          retweet_count: tweet.retweet_count || 0,
          reply_count: tweet.reply_count || 0
        }
      } as Prediction;
    });
  }, [chartFilteredTweets, user, maxEngagement]);

  // Navigate through tweets with arrow keys
  const handleTweetNavigation = useCallback((direction: 'prev' | 'next') => {
    if (filteredChartPredictions.length === 0) return;
    
    setCurrentTweetIndex(prev => {
      // If nothing selected (-1), start from beginning or end
      if (prev === -1) {
        return direction === 'next' ? 0 : filteredChartPredictions.length - 1;
      }
      if (direction === 'next') {
        return prev < filteredChartPredictions.length - 1 ? prev + 1 : 0;
      } else {
        return prev > 0 ? prev - 1 : filteredChartPredictions.length - 1;
      }
    });
  }, [filteredChartPredictions.length]);

  // Update active prediction when navigating or filter changes
  useEffect(() => {
    if (filteredChartPredictions.length > 0) {
      // Only set active prediction if currentTweetIndex is valid (not -1)
      if (currentTweetIndex >= 0 && currentTweetIndex < filteredChartPredictions.length) {
        setActivePredictions([filteredChartPredictions[currentTweetIndex].id]);
      } else if (currentTweetIndex >= filteredChartPredictions.length) {
        // Index out of bounds after filter change - reset to nothing selected
        setCurrentTweetIndex(-1);
        setActivePredictions([]);
      }
      // If currentTweetIndex is -1, keep activePredictions empty (nothing selected)
    } else {
      setActivePredictions([]);
    }
  }, [currentTweetIndex, filteredChartPredictions]);

  // Keyboard navigation for chart
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handleTweetNavigation('prev');
      } else if (e.key === 'ArrowRight') {
        handleTweetNavigation('next');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleTweetNavigation]);

  const handleLogout = () => {
    localStorage.removeItem('jwt_token');
    navigate('/wrapped');
  };

  // Filter and search tweets
  const filteredTweets = useMemo(() => {
    let result = tweets;

    // Apply filter
    if (filter === 'bitcoin') {
      result = result.filter(t => t.is_bitcoin_related);
    } else if (filter === 'predictions') {
      result = result.filter(t => t.is_prediction);
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.content.toLowerCase().includes(query)
      );
    }

    return result;
  }, [tweets, filter, searchQuery]);

  // Compute analytics insights from tweets
  const analyticsData = useMemo(() => {
    if (!tweets.length || !stats) return null;
    
    // Calculate yearly tweet breakdown
    const yearlyData: { [year: number]: { total: number; bitcoin: number } } = {};
    tweets.forEach(tweet => {
      const year = new Date(tweet.created_at).getFullYear();
      if (!yearlyData[year]) {
        yearlyData[year] = { total: 0, bitcoin: 0 };
      }
      yearlyData[year].total++;
      if (tweet.is_bitcoin_related) {
        yearlyData[year].bitcoin++;
      }
    });
    
    // Convert to array and filter for relevant years (2017-2025)
    const currentYear = new Date().getFullYear();
    const yearlyArray = Object.entries(yearlyData)
      .map(([year, data]) => ({
        year: parseInt(year),
        total: data.total,
        bitcoin: data.bitcoin
      }))
      .filter(y => y.year >= 2017 && y.year <= currentYear)
      .sort((a, b) => a.year - b.year);
    
    // Find top tweet by engagement
    const topTweet = [...tweets].sort((a, b) => {
      const engA = (a.like_count || 0) + (a.retweet_count || 0) * 2 + (a.reply_count || 0);
      const engB = (b.like_count || 0) + (b.retweet_count || 0) * 2 + (b.reply_count || 0);
      return engB - engA;
    })[0];
    
    // Determine personality type based on bitcoin percentage
    const bitcoinPercentage = stats.bitcoin_percentage || 0;
    let personalityType = 'Occasional Bitcoiner';
    let personalityEmoji = '🌱';
    if (bitcoinPercentage >= 80) {
      personalityType = 'Bitcoin Maximalist';
      personalityEmoji = '🔥';
    } else if (bitcoinPercentage >= 60) {
      personalityType = 'Bitcoin Enthusiast';
      personalityEmoji = '⚡';
    } else if (bitcoinPercentage >= 40) {
      personalityType = 'Crypto Curious';
      personalityEmoji = '🔮';
    } else if (bitcoinPercentage >= 20) {
      personalityType = 'Occasional Bitcoiner';
      personalityEmoji = '🌱';
    } else {
      personalityType = 'Traditional Finance';
      personalityEmoji = '📈';
    }
    
    // Calculate journey phase based on first tweet date
    const firstTweetYear = stats.first_tweet_date ? new Date(stats.first_tweet_date).getFullYear() : currentYear;
    const yearsActive = currentYear - firstTweetYear;
    let journeyPhase = 'New to Bitcoin';
    if (yearsActive >= 10) journeyPhase = 'OG Bitcoiner';
    else if (yearsActive >= 5) journeyPhase = 'Early Adopter';
    else if (yearsActive >= 2) journeyPhase = 'Bull Run Veteran';
    
    // Get max value for chart scaling
    const maxTweets = Math.max(...yearlyArray.map(y => y.total), 1);
    
    return {
      yearlyData: yearlyArray,
      maxTweets,
      topTweet,
      personalityType,
      personalityEmoji,
      journeyPhase,
      yearsActive,
      firstTweetYear
    };
  }, [tweets, stats]);

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format number
  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
          <p style={{ color: '#888' }}>Loading your dashboard...</p>
        </LoadingContainer>
      </Container>
    );
  }

  if (needsLogin) {
    return (
      <Container>
        <LoginContainer>
          <div className="logo-section">
            <img src="/POP_new_logopng.png" alt="ProofOfPrice" />
            <h1>ProofOfPrice</h1>
          </div>
          
          <div className="info-section">
            <h2>Your Personal Dashboard</h2>
            <p>
              Track your Bitcoin tweets, monitor your prediction accuracy, 
              and see how your insights stack up over time.
            </p>
          </div>
          
          <div className="benefits">
            <div className="benefit">
              <span className="icon">📊</span>
              <span>View all your Bitcoin-related tweets</span>
            </div>
            <div className="benefit">
              <span className="icon">🎯</span>
              <span>Track prediction accuracy over time</span>
            </div>
            <div className="benefit">
              <span className="icon">📈</span>
              <span>See engagement analytics</span>
            </div>
            <div className="benefit">
              <span className="icon">⭐</span>
              <span>Request placement on public chart</span>
            </div>
          </div>
          
          <button 
            className="login-button"
            onClick={() => window.location.href = '/api/auth/x/login?redirect=dashboard'}
          >
            <svg viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Sign in with X
          </button>
          
          <p className="footer-note">
            We only request read access to your public tweets. 
            We never post on your behalf.
          </p>
        </LoginContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <h2>⚠️ {error}</h2>
          <p>Please sign in again to access your dashboard.</p>
          <button onClick={() => window.location.href = '/api/auth/x/login'}>
            <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px', fill: 'currentColor' }}>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Sign in with X
          </button>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
    <Container>
      <Header>
        <Logo onClick={() => navigate('/')}>
          <img src="/POP_new_logopng.png" alt="ProofOfPrice" />
          <span>ProofOfPrice</span>
        </Logo>
        
        <UserInfo>
          <UserDetails>
            <div className="name">{user?.name}</div>
            <div className="handle">@{user?.x_handle}</div>
          </UserDetails>
          {user?.profile_image && (
            <Avatar src={user.profile_image} alt={user.name} />
          )}
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        </UserInfo>
      </Header>

      <MainContent>
        <WelcomeSection>
          <h1>📊 Your Dashboard</h1>
          <p>Search through your tweet history and submit your Bitcoin predictions to the public chart</p>
          {stats && (
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
              📁 {formatNumber(stats.total_tweets)} tweets loaded • 
              {stats.first_tweet_date && stats.last_tweet_date && (
                <> {new Date(stats.first_tweet_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} → {new Date(stats.last_tweet_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</>
              )}
            </p>
          )}
        </WelcomeSection>
        
        {/* How it works - collapsible */}
        <HowItWorks>
          <div className="header">
            <span>💡 How it works</span>
          </div>
          <div className="steps">
            <div className="step">
              <span className="number">1</span>
              <span className="text">Browse your Bitcoin tweets below</span>
            </div>
            <div className="step">
              <span className="number">2</span>
              <span className="text">Find a tweet with a price prediction</span>
            </div>
            <div className="step">
              <span className="number">3</span>
              <span className="text">Click "📤 Request" to submit for review</span>
            </div>
            <div className="step">
              <span className="number">✓</span>
              <span className="text">We'll add approved predictions to the public chart!</span>
            </div>
          </div>
        </HowItWorks>

        {/* Read-only banner for free tier */}
        {!user?.dashboard_unlocked && (
          <ReadOnlyBanner>
            <div className="message">
              <span>👁️</span>
              <div>
                <strong>Read-Only Mode</strong>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#aaa' }}>
                  Upgrade to manage your predictions and request public chart placement
                </p>
              </div>
            </div>
            <button onClick={() => navigate('/pricing')}>
              ⚡ Unlock Full Access
            </button>
          </ReadOnlyBanner>
        )}

        {/* 🎯 YOUR ACTIVE PREDICTIONS - Prominent display */}
        {officialPredictions.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(247, 147, 26, 0.15), rgba(255, 107, 0, 0.1))',
            border: '2px solid rgba(247, 147, 26, 0.5)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              color: '#f7931a', 
              margin: '0 0 16px 0', 
              fontSize: '1.3rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span>🎯</span> Your Predictions on the Public Chart
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {officialPredictions.map((pred) => {
                const isPending = pred.success_status === 'pending' || !pred.success_status;
                const isSuccess = pred.success_status === 'success';
                const isMiss = pred.success_status === 'miss';
                const isOwned = !!pred.owned_at;
                const targetDate = pred.target_time ? new Date(pred.target_time) : null;
                const predDate = pred.prediction_made ? new Date(pred.prediction_made) : null;
                const daysLeft = targetDate ? Math.ceil((targetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
                
                return (
                  <div key={pred.id} style={{
                    background: isPending 
                      ? 'rgba(255, 193, 7, 0.1)' 
                      : isSuccess 
                        ? 'rgba(76, 175, 80, 0.1)' 
                        : 'rgba(244, 67, 54, 0.1)',
                    border: `1px solid ${isPending ? 'rgba(255, 193, 7, 0.4)' : isSuccess ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
                    borderRadius: '12px',
                    padding: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: '1.8rem', 
                          fontWeight: '800',
                          background: 'linear-gradient(135deg, #f7931a, #ff6b00)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                          ${Number(pred.price_prediction).toLocaleString()}
                        </div>
                        <div style={{ color: '#888', fontSize: '0.85rem' }}>
                          {pred.prediction_type?.toUpperCase() || 'TARGET'} prediction
                          {predDate && <span> • Made {predDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {targetDate && (
                          <div style={{ 
                            fontSize: '1rem', 
                            fontWeight: '600',
                            color: isPending ? '#ffc107' : isSuccess ? '#4caf50' : '#f44336'
                          }}>
                            Target: {targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginTop: '4px',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          background: isPending 
                            ? 'rgba(255, 193, 7, 0.2)' 
                            : isSuccess 
                              ? 'rgba(76, 175, 80, 0.2)' 
                              : 'rgba(244, 67, 54, 0.2)',
                          color: isPending ? '#ffc107' : isSuccess ? '#4caf50' : '#f44336'
                        }}>
                          {isPending ? '⏳' : isSuccess ? '✅' : '❌'}
                          {isPending ? (daysLeft !== null && daysLeft > 0 ? `${daysLeft} days left` : 'Awaiting evaluation') : isSuccess ? 'Correct!' : 'Missed'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Owner explanation if already owned */}
                    {isOwned && pred.owner_explanation && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: 'rgba(0,0,0,0.2)',
                        borderRadius: '8px',
                        borderLeft: `3px solid ${isSuccess ? '#4caf50' : isMiss ? '#f44336' : '#ffc107'}`
                      }}>
                        <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>
                          💬 Your explanation:
                        </div>
                        <div style={{ color: '#ddd', fontSize: '0.9rem', lineHeight: '1.4' }}>
                          {pred.owner_explanation}
                        </div>
                      </div>
                    )}
                    
                    {/* Action buttons */}
                    <div style={{
                      marginTop: '12px',
                      display: 'flex',
                      gap: '10px',
                      flexWrap: 'wrap'
                    }}>
                      {!isOwned ? (
                        <button
                          onClick={() => {
                            setOwningPrediction(pred);
                            setOwnExplanation('');
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #9c27b0, #673ab7)',
                            border: 'none',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          🏆 Own It
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setOwningPrediction(pred);
                            setOwnExplanation(pred.owner_explanation || '');
                          }}
                          style={{
                            background: 'rgba(156, 39, 176, 0.2)',
                            border: '1px solid rgba(156, 39, 176, 0.4)',
                            color: '#ce93d8',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          ✏️ Edit
                        </button>
                      )}
                      
                      {/* Share to Twitter */}
                      <a
                        href={getTwitterShareUrl(pred)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: 'rgba(29, 161, 242, 0.2)',
                          border: '1px solid rgba(29, 161, 242, 0.4)',
                          color: '#1DA1F2',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          textDecoration: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        Share on X
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ 
              marginTop: '12px', 
              fontSize: '0.8rem', 
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>💡</span>
              <span>These predictions appear on the <a href="/btc" style={{ color: '#f7931a' }}>public chart</a> for everyone to see!</span>
            </div>
          </div>
        )}

        {/* Personal Bitcoin Chart with User's Tweets - Same layout as main chart */}
        {chartData.length > 0 && chartPredictions.length > 0 && (
          <ChartSection>
            <h2>📈 Your Bitcoin Timeline</h2>
            
            {/* Compact Filter Row - simplified on mobile */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: isMobile ? '8px' : '12px', 
              alignItems: 'center',
              marginBottom: '16px',
              padding: isMobile ? '10px' : '12px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px'
            }}>
              {/* Content Filter - always visible */}
              <select
                value={filter}
                onChange={(e) => { setFilter(e.target.value as any); setCurrentTweetIndex(-1); }}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  padding: isMobile ? '8px 10px' : '6px 10px',
                  color: '#fff',
                  fontSize: isMobile ? '0.9rem' : '0.85rem',
                  cursor: 'pointer',
                  flex: isMobile ? '1' : 'none',
                  minWidth: isMobile ? '0' : 'auto'
                }}
              >
                <option value="all" style={{ background: '#1a1a1a' }}>All ({tweets.length})</option>
                <option value="bitcoin" style={{ background: '#1a1a1a' }}>Bitcoin ({tweets.filter(t => t.is_bitcoin_related).length})</option>
                <option value="predictions" style={{ background: '#1a1a1a' }}>Predictions ({tweets.filter(t => t.is_prediction).length})</option>
              </select>
              
              {/* Time Period - always visible */}
              <select
                value={timePeriod}
                onChange={(e) => { setTimePeriod(e.target.value as any); setCurrentTweetIndex(-1); }}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  padding: isMobile ? '8px 10px' : '6px 10px',
                  color: '#fff',
                  fontSize: isMobile ? '0.9rem' : '0.85rem',
                  cursor: 'pointer',
                  flex: isMobile ? '1' : 'none',
                  minWidth: isMobile ? '0' : 'auto'
                }}
              >
                <option value="all" style={{ background: '#1a1a1a' }}>All Time</option>
                <option value="week" style={{ background: '#1a1a1a' }}>Last Week</option>
                <option value="month" style={{ background: '#1a1a1a' }}>Last Month</option>
                <option value="year" style={{ background: '#1a1a1a' }}>Last Year</option>
              </select>
              
              {/* Desktop-only filters */}
              {!isMobile && (
                <>
                  {/* Search */}
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      fontSize: '0.85rem',
                      width: '140px'
                    }}
                  />
              
                  {/* Type Filter */}
                  <select
                    value={tweetTypeFilter}
                    onChange={(e) => { setTweetTypeFilter(e.target.value as any); setCurrentTweetIndex(-1); }}
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      color: '#fff',
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all" style={{ background: '#1a1a1a' }}>All Types</option>
                    <option value="original" style={{ background: '#1a1a1a' }}>Original</option>
                    <option value="replies" style={{ background: '#1a1a1a' }}>Replies</option>
                    <option value="retweets" style={{ background: '#1a1a1a' }}>Retweets</option>
                  </select>
                </>
              )}
              
              {/* Top Engaged Toggle */}
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: showTopOnly ? '#f7931a' : '#888'
              }}>
                <input
                  type="checkbox"
                  checked={showTopOnly}
                  onChange={(e) => { setShowTopOnly(e.target.checked); setCurrentTweetIndex(-1); }}
                  style={{ accentColor: '#f7931a' }}
                />
                Top Engaged
              </label>
              
              {/* Custom Keywords (inline) */}
              {customKeywords && (
                <span style={{ 
                  background: 'rgba(247, 147, 26, 0.2)',
                  border: '1px solid rgba(247, 147, 26, 0.4)',
                  borderRadius: '12px',
                  padding: '4px 10px',
                  fontSize: '0.75rem',
                  color: '#f7931a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  "{customKeywords}"
                  <button 
                    onClick={() => { setCustomKeywords(''); setCurrentTweetIndex(-1); }}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#f7931a', 
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '0.9rem'
                    }}
                  >×</button>
                </span>
              )}
              
              {/* More Filters Button */}
              <button 
                onClick={() => setShowCustomFilter(!showCustomFilter)}
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  color: '#888',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                {showCustomFilter ? '− Less' : '+ More'}
              </button>
            </div>
            
            {/* Expanded Custom Filter */}
            {showCustomFilter && (
                <div style={{ 
                  background: 'rgba(0,0,0,0.3)', 
                  borderRadius: '8px', 
                  padding: '12px',
                  border: '1px solid rgba(247, 147, 26, 0.2)'
                }}>
                  <div style={{ marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="e.g., mstr + prediction | saylor + bitcoin"
                      value={customKeywords}
                      onChange={(e) => { setCustomKeywords(e.target.value); setCurrentTweetIndex(-1); }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(247, 147, 26, 0.3)',
                        background: 'rgba(0,0,0,0.3)',
                        color: '#fff',
                        fontSize: '0.85rem'
                      }}
                    />
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '8px' }}>
                    💡 Use <code style={{ color: '#f7931a' }}>+</code> for AND, <code style={{ color: '#f7931a' }}>|</code> for OR. 
                    Example: <code style={{ color: '#888' }}>mstr + buy</code> finds tweets with both words.
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      onClick={saveCustomFilter}
                      disabled={!customKeywords.trim()}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        background: customKeywords.trim() ? '#f7931a' : '#333',
                        color: customKeywords.trim() ? '#000' : '#666',
                        fontSize: '0.75rem',
                        cursor: customKeywords.trim() ? 'pointer' : 'not-allowed'
                      }}
                    >
                      💾 Save Filter
                    </button>
                    {customKeywords && (
                      <button
                        onClick={() => { setCustomKeywords(''); setCurrentTweetIndex(-1); }}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '4px',
                          border: '1px solid #666',
                          background: 'transparent',
                          color: '#888',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>
                  
                  {/* Saved Filters */}
                  {savedFilters.length > 0 && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                      <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '6px' }}>Saved Filters:</div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {savedFilters.map((sf, idx) => (
                          <div 
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              background: customKeywords === sf.keywords ? 'rgba(247, 147, 26, 0.3)' : 'rgba(0,0,0,0.3)',
                              borderRadius: '4px',
                              border: customKeywords === sf.keywords ? '1px solid #f7931a' : '1px solid rgba(255,255,255,0.1)',
                              fontSize: '0.75rem'
                            }}
                          >
                            <span 
                              onClick={() => { setCustomKeywords(sf.keywords); setCurrentTweetIndex(-1); }}
                              style={{ cursor: 'pointer', color: '#ddd' }}
                            >
                              {sf.name}
                            </span>
                            <span 
                              onClick={() => deleteSavedFilter(idx)}
                              style={{ cursor: 'pointer', color: '#666', marginLeft: '4px' }}
                            >
                              ×
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            
            {/* Active filter summary - simplified on mobile */}
            {!isMobile && (
            <div style={{ 
              fontSize: '0.8rem', 
              color: '#888', 
              marginBottom: '12px',
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '8px',
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <span style={{ color: '#666' }}>Showing:</span>
              <span style={{ color: '#f7931a', fontWeight: 500 }}>
                {filter === 'all' ? 'All' : filter === 'bitcoin' ? '₿ Bitcoin' : '🎯 Predictions'}
              </span>
              <span style={{ color: '#444' }}>→</span>
              <span style={{ color: '#f7931a', fontWeight: 500 }}>
                {tweetTypeFilter === 'all' ? 'All types' : 
                 tweetTypeFilter === 'original' ? '✍️ Original' : 
                 tweetTypeFilter === 'replies' ? '💬 Replies' : '🔄 Retweets'}
              </span>
              {timePeriod !== 'all' && (
                <>
                  <span style={{ color: '#444' }}>→</span>
                  <span style={{ color: '#f7931a', fontWeight: 500 }}>
                    {timePeriod === 'week' ? '📅 Last week' : timePeriod === 'month' ? '📅 Last month' : '📅 Last year'}
                  </span>
                </>
              )}
              {showTopOnly && (
                <>
                  <span style={{ color: '#444' }}>→</span>
                  <span style={{ color: '#ff6b00', fontWeight: 500 }}>🔥 Top 10%</span>
                </>
              )}
              {customKeywords && (
                <>
                  <span style={{ color: '#444' }}>→</span>
                  <span style={{ color: '#9b59b6', fontWeight: 500 }}>🔍 "{customKeywords}"</span>
                </>
              )}
              <span style={{ marginLeft: 'auto', color: '#f7931a', fontWeight: 600 }}>
                {filteredChartPredictions.length} tweets
              </span>
            </div>
            )}
            
            {/* Legend explaining the dots - hidden on mobile */}
            {!isMobile && (
            <ChartLegend>
              <div className="legend-title">
                <span>💡</span> Understanding your tweet timeline
              </div>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="dot dot-active"></div>
                  <span>Selected tweet (shows details)</span>
                </div>
                <div className="legend-item">
                  <div className="dot dot-inactive" style={{ width: '6px', height: '6px' }}></div>
                  <span>Low engagement tweets</span>
                </div>
                <div className="legend-item">
                  <div className="dot dot-inactive" style={{ width: '12px', height: '12px', background: 'rgba(247, 147, 26, 0.8)' }}></div>
                  <span>High engagement tweets</span>
                </div>
              </div>
              <div className="legend-description">
                <strong>Dot size = engagement level</strong> (likes + retweets × 2 + replies). 
                Bigger, brighter dots = more engagement. Each dot is positioned at the 
                <strong> exact Bitcoin price</strong> when you tweeted. Use "🔥 Top Engaged" to find your best performing tweets!
              </div>
            </ChartLegend>
            )}
            
            {isMobile ? (
              /* Mobile Layout - Single column with compact chart */
              <>
                <MobileChartArea>
                  <MobileChartWrapper
                    predictions={filteredChartPredictions}
                    activePredictions={activePredictions}
                    chartData={chartData}
                    isDarkMode={true}
                    theme={darkTheme}
                  />
                </MobileChartArea>
                
                {/* Mobile Tweet List */}
                <MobileTweetList>
                  {filteredChartPredictions.slice(0, 20).map((pred, index) => {
                    const tweetData = pred.tweet_data as any;
                    return (
                      <MobileTweetCard 
                        key={pred.id}
                        isActive={activePredictions.includes(pred.id)}
                        onClick={() => {
                          if (activePredictions.includes(pred.id)) {
                            setActivePredictions([]);
                            setCurrentTweetIndex(-1);
                          } else {
                            setCurrentTweetIndex(index);
                            setActivePredictions([pred.id]);
                          }
                        }}
                      >
                        <div className="tweet-header">
                          <span className="date">
                            {new Date(pred.created_at || '').toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                          {tweetData?.engagement_score > 0 && (
                            <span className="engagement">🔥 {tweetData.engagement_score}</span>
                          )}
                        </div>
                        <div className="tweet-text">{pred.quote}</div>
                        <div className="tweet-stats">
                          {tweetData?.like_count > 0 && <span>❤️ {tweetData.like_count}</span>}
                          {tweetData?.retweet_count > 0 && <span>🔄 {tweetData.retweet_count}</span>}
                        </div>
                      </MobileTweetCard>
                    );
                  })}
                  {filteredChartPredictions.length > 20 && (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '0.8rem' }}>
                      Showing 20 of {filteredChartPredictions.length} tweets
                    </div>
                  )}
                </MobileTweetList>
              </>
            ) : (
              /* Desktop Layout - Side by side */
              <ChartLayoutGrid>
                {/* Chart Area - matches main app */}
                <ChartArea>
                  <PriceChart
                    predictions={filteredChartPredictions}
                    activePredictions={activePredictions}
                    chartData={chartData}
                    isDarkMode={true}
                    theme={darkTheme}
                    showAllDots={true}
                    preserveZoomOnSelection={true}
                  />
                </ChartArea>
              
                {/* Custom Tweet Panel for Dashboard */}
                <TweetPanel>
                  <TweetPanelHeader>
                    <h3>🐦 {filter === 'all' ? 'All' : filter === 'bitcoin' ? 'Bitcoin' : 'Prediction'} Tweets</h3>
                    <div className="stats">
                      <span><span className="count">{filteredChartPredictions.length}</span> on chart</span>
                  </div>
                </TweetPanelHeader>
                <ChartTweetList>
                  {filteredChartPredictions.map((pred, index) => {
                    const tweetData = pred.tweet_data as any;
                    return (
                    <ChartTweetCard 
                      key={pred.id}
                      isActive={activePredictions.includes(pred.id)}
                      onClick={() => {
                        // Toggle: if already active, deselect; otherwise select
                        if (activePredictions.includes(pred.id)) {
                          setActivePredictions([]);
                          setCurrentTweetIndex(-1); // -1 means nothing selected
                        } else {
                          setCurrentTweetIndex(index);
                          setActivePredictions([pred.id]);
                        }
                      }}
                    >
                      <div className="tweet-date">
                        {new Date(pred.created_at || '').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                        {tweetData?.engagement_score > 0 && (
                          <span style={{ marginLeft: '8px', color: tweetData.engagement_level > 0.5 ? '#f7931a' : '#666' }}>
                            🔥 {tweetData.engagement_score}
                          </span>
                        )}
                      </div>
                      <div className="tweet-text">{pred.quote}</div>
                      <div className="tweet-meta">
                        {tweetData?.like_count > 0 && <span>❤️ {tweetData.like_count}</span>}
                        {tweetData?.retweet_count > 0 && <span>🔄 {tweetData.retweet_count}</span>}
                        {tweetData?.reply_count > 0 && <span>💬 {tweetData.reply_count}</span>}
                        {pred.source_url && (
                          <span 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(pred.source_url, '_blank');
                            }}
                            style={{ color: '#f7931a', cursor: 'pointer' }}
                          >
                            🔗 View on X
                          </span>
                        )}
                      </div>
                    </ChartTweetCard>
                  );
                  })}
                </ChartTweetList>
              </TweetPanel>
            </ChartLayoutGrid>
            )}
            
            {/* Navigation controls - hidden on mobile */}
            {!isMobile && (
              <ChartControls>
                <NavigationButtons>
                  <button onClick={() => handleTweetNavigation('prev')}>
                    ← Previous
                  </button>
                  <span className="counter">
                    {currentTweetIndex >= 0 ? currentTweetIndex + 1 : '-'} / {filteredChartPredictions.length}
                  </span>
                  <button onClick={() => handleTweetNavigation('next')}>
                    Next →
                  </button>
                </NavigationButtons>
                <ChartInfo>
                  <span>{filteredChartPredictions.length}</span> tweets on chart
                  <div className="hint">💡 Use ← → arrow keys to navigate • Click tweet to select/deselect</div>
                </ChartInfo>
              </ChartControls>
            )}
          </ChartSection>
        )}

        {/* 📊 Analytics & Insights Section */}
        {analyticsData && stats && (
          <AnalyticsSection>
            <h2><span>📊</span> Your Tweet Analytics</h2>
            
            {/* Key Stats Grid */}
            <InsightsGrid>
              <InsightCard>
                <div className="stat-value">{formatNumber(stats.total_tweets)}</div>
                <div className="stat-label">Total Tweets</div>
                <div className="stat-detail">Since {analyticsData.firstTweetYear}</div>
              </InsightCard>
              
              <InsightCard>
                <div className="stat-value">{formatNumber(stats.bitcoin_tweets)}</div>
                <div className="stat-label">Bitcoin Tweets</div>
                <div className="stat-detail">
                  {stats.bitcoin_percentage}% of total
                  {stats.bitcoin_percentage >= 50 ? ' 🔥' : stats.bitcoin_percentage >= 20 ? ' ⚡' : ''}
                </div>
              </InsightCard>
              
              <InsightCard>
                <div className="stat-value">{formatNumber(stats.avg_engagement)}</div>
                <div className="stat-label">Avg Engagement</div>
                <div className="stat-detail">
                  Per tweet {stats.avg_engagement >= 100 ? '🔥' : stats.avg_engagement >= 20 ? '👍' : ''}
                </div>
              </InsightCard>
              
              <InsightCard>
                <div className="stat-value">{analyticsData.yearlyData.length}</div>
                <div className="stat-label">Years of Data</div>
                <div className="stat-detail">{analyticsData.firstTweetYear} → {new Date().getFullYear()}</div>
              </InsightCard>
              
              <InsightCard gradient={stats.predictions_count > 0 
                ? "linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(56, 142, 60, 0.1))" 
                : "linear-gradient(135deg, rgba(100, 100, 100, 0.2), rgba(80, 80, 80, 0.1))"
              }>
                <div className="stat-value" style={{ 
                  background: stats.predictions_count > 0 
                    ? 'linear-gradient(135deg, #4caf50, #81c784)'
                    : 'linear-gradient(135deg, #888, #aaa)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>{stats.predictions_count > 0 ? `${stats.accuracy_rate}%` : '—'}</div>
                <div className="stat-label">Prediction Accuracy</div>
                <div className="stat-detail">
                  {stats.predictions_count > 0 
                    ? `${stats.predictions_correct} correct / ${stats.predictions_correct + stats.predictions_incorrect} evaluated`
                    : 'Submit predictions below!'}
                </div>
              </InsightCard>
            </InsightsGrid>
            
            {/* Yearly Tweet Chart */}
            {analyticsData.yearlyData.length > 0 && (
              <JourneyTimeline>
                <h3><span>📈</span> Bitcoin Tweets Over Time</h3>
                <YearlyChart>
                  {analyticsData.yearlyData.map((yearData) => (
                    <YearBar 
                      key={yearData.year} 
                      height={(yearData.bitcoin / analyticsData.maxTweets) * 100}
                      isCurrentYear={yearData.year === new Date().getFullYear()}
                    >
                      <div className="bar-container">
                        <div className="count">{yearData.bitcoin}</div>
                        <div className="bar" />
                      </div>
                      <div className="year-label">{yearData.year.toString().slice(-2)}</div>
                    </YearBar>
                  ))}
                </YearlyChart>
              </JourneyTimeline>
            )}
            
            {/* Top Tweet */}
            {analyticsData.topTweet && (
              <div>
                <h3 style={{ 
                  color: '#f7931a', 
                  fontSize: '1rem', 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🏆</span> Your Top Performing Tweet
                </h3>
                <TopTweetCard onClick={() => {
                  if (user) {
                    window.open(`https://x.com/${user.x_handle}/status/${analyticsData.topTweet.tweet_id}`, '_blank');
                  }
                }}>
                  <div className="tweet-header">
                    <div className="avatar">
                      {user?.profile_image ? (
                        <img src={user.profile_image} alt={user.name} />
                      ) : (
                        user?.name?.charAt(0).toUpperCase() || 'U'
                      )}
                    </div>
                    <div className="user-info">
                      <div className="name">{user?.name || 'User'}</div>
                      <div className="handle">@{user?.x_handle}</div>
                    </div>
                    <div className="badge">🏆 TOP</div>
                  </div>
                  <div className="tweet-text">
                    {analyticsData.topTweet.content.length > 280 
                      ? analyticsData.topTweet.content.substring(0, 280) + '...'
                      : analyticsData.topTweet.content}
                  </div>
                  <div className="tweet-stats">
                    <span className="likes">❤️ {formatNumber(analyticsData.topTweet.like_count || 0)}</span>
                    <span>🔄 {formatNumber(analyticsData.topTweet.retweet_count || 0)}</span>
                    <span>💬 {formatNumber(analyticsData.topTweet.reply_count || 0)}</span>
                    <span style={{ marginLeft: 'auto', color: '#71767b' }}>
                      📅 {new Date(analyticsData.topTweet.created_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                    </span>
                  </div>
                </TopTweetCard>
              </div>
            )}
            
            {/* Revisit Wrapped Button */}
            <WrappedButton onClick={() => navigate(`/wrapped?user=${user?.x_handle}`)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 8v8m0-8l4 4m-4-4l-4 4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
              Revisit Your Bitcoin Wrapped
            </WrappedButton>
          </AnalyticsSection>
        )}

        {/* 🔍 Tweet Analysis Section */}
        {potentialPredictions.length > 0 && (
          <PredictionFinderSection>
            <h3>
              <span>🔍</span> 
              Tweet Analysis
              <span style={{ 
                fontSize: '0.75rem', 
                background: 'rgba(156, 39, 176, 0.3)', 
                padding: '4px 8px', 
                borderRadius: '12px',
                marginLeft: '10px'
              }}>
                {potentialPredictions.length} to review
              </span>
            </h3>
            <p className="description">
              Our word filter flagged these tweets as potential price predictions.
              Review and <strong>request a review</strong> for real predictions, 
              or dismiss false positives.
            </p>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => setShowPredictionFinder(!showPredictionFinder)}
                style={{
                  background: 'rgba(156, 39, 176, 0.2)',
                  border: '1px solid rgba(156, 39, 176, 0.4)',
                  color: '#ce93d8',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {showPredictionFinder ? '▼ Hide' : '▶ Review Tweets'}
              </button>
            </div>
            
            {showPredictionFinder && (
              <div>
                {potentialPredictions.map((pred, idx) => (
                  <PotentialPredictionCard 
                    key={pred.tweet.tweet_id} 
                    requested={requestedPredictions.has(pred.tweet.tweet_id)}
                  >
                    <div className="tweet-date">
                      {new Date(pred.tweet.created_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric'
                      })}
                      <span style={{ 
                        marginLeft: '10px', 
                        background: pred.confidence === 'high' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 193, 7, 0.3)',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '0.7rem',
                        color: pred.confidence === 'high' ? '#81c784' : '#ffd54f'
                      }}>
                        {pred.confidence === 'high' ? '✓ High confidence' : '? Medium confidence'}
                      </span>
                    </div>
                    <div className="tweet-text">{pred.tweet.content}</div>
                    <div className="detected-info">
                      <span>Detected: </span>
                      <span className="price">${pred.detectedPrice.toLocaleString()}</span>
                      <span className="type">({pred.predictionType})</span>
                    </div>
                    <div className="actions">
                      {requestedPredictions.has(pred.tweet.tweet_id) ? (
                        <button className="requested" disabled>
                          ✓ Review Requested
                        </button>
                      ) : (
                        <>
                          <button 
                            className="request-btn"
                            onClick={() => handleRequestPrediction(
                              pred.tweet, 
                              pred.detectedPrice, 
                              pred.predictionType
                            )}
                            disabled={!user?.dashboard_unlocked}
                            title={!user?.dashboard_unlocked ? 'Unlock dashboard to request' : 'Request admin review to add to chart'}
                          >
                            📤 Request Review
                          </button>
                          <button
                            onClick={() => handleDismissPrediction(pred.tweet.tweet_id)}
                            style={{
                              background: 'rgba(244, 67, 54, 0.1)',
                              border: '1px solid rgba(244, 67, 54, 0.3)',
                              color: '#e57373',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                            title="Not a prediction - hide this tweet"
                          >
                            ❌ Not a Prediction
                          </button>
                        </>
                      )}
                      {!user?.dashboard_unlocked && (
                        <span style={{ fontSize: '0.75rem', color: '#888' }}>
                          Unlock to request
                        </span>
                      )}
                    </div>
                  </PotentialPredictionCard>
                ))}
              </div>
            )}
          </PredictionFinderSection>
        )}

        {/* Tweet Discovery Section - Main area for finding predictions */}
        <Section>
          <SectionHeader>
            <h2><span>🔍</span> Find Your Predictions</h2>
            <span style={{ fontSize: '0.9rem', color: '#888' }}>
              {filteredTweets.length} tweets • Click "📤 Request" to submit
            </span>
          </SectionHeader>

          {filteredTweets.length > 0 ? (
            <TweetList>
              {filteredTweets.map(tweet => (
                <TweetCard 
                  key={tweet.tweet_id}
                  isPrediction={tweet.is_prediction}
                  isSelected={selectedTweet === tweet.tweet_id}
                  onClick={() => setSelectedTweet(
                    selectedTweet === tweet.tweet_id ? null : tweet.tweet_id
                  )}
                >
                  <TweetHeader>
                    <TweetDate>{formatDate(tweet.created_at)}</TweetDate>
                    <TweetBadges>
                      {tweet.is_bitcoin_related && <Badge type="bitcoin">₿ Bitcoin</Badge>}
                      {tweet.is_prediction && <Badge type="prediction">🎯 Prediction</Badge>}
                      {requestedPredictions.has(tweet.tweet_id) && (
                        <Badge type="correct">✓ Requested</Badge>
                      )}
                    </TweetBadges>
                  </TweetHeader>
                  
                  <TweetContent>
                    {tweet.content.length > 280 
                      ? tweet.content.substring(0, 280) + '...' 
                      : tweet.content
                    }
                  </TweetContent>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginTop: '10px'
                  }}>
                    <TweetStats>
                      <span>❤️ {formatNumber(tweet.like_count)}</span>
                      <span>🔄 {formatNumber(tweet.retweet_count)}</span>
                      <span>💬 {formatNumber(tweet.reply_count)}</span>
                    </TweetStats>
                    
                    {/* Request button - always visible */}
                    {!requestedPredictions.has(tweet.tweet_id) ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleManualTweetRequest(tweet);
                        }}
                        style={{
                          background: selectedTweet === tweet.tweet_id 
                            ? 'linear-gradient(135deg, #f7931a, #ffb74d)' 
                            : 'rgba(247, 147, 26, 0.2)',
                          border: '1px solid rgba(247, 147, 26, 0.4)',
                          color: selectedTweet === tweet.tweet_id ? '#000' : '#f7931a',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s'
                        }}
                      >
                        📤 Request
                      </button>
                    ) : (
                      <span style={{ 
                        color: '#4caf50', 
                        fontSize: '0.75rem', 
                        fontWeight: 600 
                      }}>
                        ✓ Requested
                      </span>
                    )}
                  </div>
                </TweetCard>
              ))}
            </TweetList>
          ) : (
            <EmptyState>
              <div className="icon">🔍</div>
              <p>No tweets found matching your criteria</p>
            </EmptyState>
          )}
        </Section>
      </MainContent>
      
      {/* Own Prediction Modal */}
      {owningPrediction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '1px solid rgba(247, 147, 26, 0.3)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h3 style={{ 
              color: '#f7931a', 
              margin: '0 0 8px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              {owningPrediction.owned_at ? '✏️ Edit Explanation' : '🏆 Own This Prediction'}
            </h3>
            <p style={{ color: '#888', fontSize: '0.9rem', margin: '0 0 20px 0' }}>
              {owningPrediction.owned_at 
                ? 'Update your explanation or clear it to remove.'
                : `Take ownership of your $${Number(owningPrediction.price_prediction).toLocaleString()} prediction. Add an explanation to share with your followers.`
              }
            </p>
            
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: '700',
                color: '#f7931a'
              }}>
                ${Number(owningPrediction.price_prediction).toLocaleString()}
              </div>
              <div style={{ color: '#888', fontSize: '0.85rem' }}>
                {owningPrediction.prediction_type?.toUpperCase()} • Target: {owningPrediction.target_time ? new Date(owningPrediction.target_time).toLocaleDateString() : 'N/A'}
              </div>
              <div style={{
                marginTop: '8px',
                padding: '4px 10px',
                borderRadius: '12px',
                display: 'inline-block',
                fontSize: '0.8rem',
                fontWeight: '600',
                background: owningPrediction.success_status === 'success' 
                  ? 'rgba(76, 175, 80, 0.2)' 
                  : owningPrediction.success_status === 'miss'
                    ? 'rgba(244, 67, 54, 0.2)'
                    : 'rgba(255, 193, 7, 0.2)',
                color: owningPrediction.success_status === 'success' 
                  ? '#4caf50' 
                  : owningPrediction.success_status === 'miss'
                    ? '#f44336'
                    : '#ffc107'
              }}>
                {owningPrediction.success_status === 'success' ? '✅ Correct' : owningPrediction.success_status === 'miss' ? '❌ Missed' : '⏳ Pending'}
              </div>
            </div>
            
            <label style={{ display: 'block', marginBottom: '16px' }}>
              <span style={{ color: '#aaa', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>
                Your explanation (optional):
              </span>
              <textarea
                value={ownExplanation}
                onChange={(e) => setOwnExplanation(e.target.value)}
                placeholder={owningPrediction.success_status === 'miss' 
                  ? "What went wrong? What did you learn?"
                  : owningPrediction.success_status === 'success'
                    ? "What gave you the conviction? Share your thesis."
                    : "Why do you believe in this prediction?"
                }
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(0, 0, 0, 0.3)',
                  color: '#fff',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <span style={{ color: '#666', fontSize: '0.75rem' }}>
                This will be shown on your dashboard and included when you share to X.
              </span>
            </label>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setOwningPrediction(null);
                  setOwnExplanation('');
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#888',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleOwnPrediction}
                disabled={isOwning}
                style={{
                  background: 'linear-gradient(135deg, #9c27b0, #673ab7)',
                  border: 'none',
                  color: 'white',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  cursor: isOwning ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  opacity: isOwning ? 0.7 : 1
                }}
              >
                {isOwning ? 'Saving...' : owningPrediction.owned_at ? '💾 Update' : '🏆 Own It'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
    </ThemeProvider>
  );
};

export default UserDashboard;
